import React, { useState, useEffect } from 'react';

import { Box } from '@chakra-ui/react';
import { Container } from '@material-ui/core';
import { getDeerEngagementTemplatePreviewRoute } from 'Deer/constants/nonDeerRoutes';
import { setL2Visible } from 'Deer/slice/DeerNav.slice';
import APIError from 'components/AlertScreens/APIError';
import EmptyError from 'components/AlertScreens/EmptyError';
import ListSkeletonLoader from 'components/AlertScreens/ListSkeletonLoader';
import FooterActions from 'components/Review/FooterActions/FooterActions';
import HeaderActions from 'components/Review/HeaderActions/HeaderActions';
import NoQuestionsScreen from 'components/Templates/NoQuestionsScreen';
import useAsync from 'components/hooks/useAsync';
import { EngagementTemplateQuestion } from 'models/templates/engagement';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import HeaderPrimaryTextComponent from 'screens/admin/common/TemplateCommons/HeaderPrimaryTextComponent';
import { useQuestionListSidePanel } from 'screens/admin/common/TemplateCommons/QuestionListSidepanel/context/QuestionListSidepanelContext';
import styled from 'styled-components';
import { StyledLink } from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { NeedAttention } from 'assets/assetsV1/iconComponents';

import { QUESTION_ORDER_ACTIONS } from 'constants/ProjectConstants';
import { goToEngagementTemplateListRoute, insertDisplayOrderInArray, swapElementsInArray } from 'constants/helper';
import { PreviewIcon } from 'constants/icons';
import { KH_LINKS } from 'constants/knowledgeHubLinks';
import { isNetchexLogin } from 'constants/netChexConstants';
import { routes } from 'constants/routes';
import theme from 'constants/theme';

import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';
import { getTemplateStructure, createTemplateStructure, updateTemplateStructure } from 'services/templates/engagement';

import { Typography11, Typography13 } from 'uiToolkit/Typography';
import { TextLabel } from 'uiToolkitV1/Typography';

import { useEngagementTemplate } from '../Context/EngagementContext';
import EngagementQuestionRow from './EngagementQuestionRow';

const BodyContainer = styled.div`
  flex-grow: 1;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    width: 0;
    background: transparent;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledContainer = styled(Container)`
  margin-bottom: 2rem;
  max-width: 800px;
`;

const NEW_TEMPLATE = 'new';

const EngagementTemplateForm = () => {
  const [saveLoading, setSaveLoading] = useState(null);
  const [selectedId, setSelectedId] = React.useState();

  const history = useHistory();
  const dispatch = useDispatch();

  const { showSidepanel } = useQuestionListSidePanel();
  const {
    state: templateData,
    setData,
    clearData,
    addQuestion,
    removeQuestion,
    setQuestions,
  } = useEngagementTemplate();

  const { data, isLoading, isError, run } = useAsync();

  const { templateId } = useParams();
  const { templateQuestions, name, isEditable = true } = templateData;

  const getTemplateMeta = () => {
    run(getTemplateStructure({ templateId }));
  };

  useEffect(() => {
    if (templateId && templateId !== NEW_TEMPLATE) {
      getTemplateMeta();
    }
  }, []);

  useEffect(() => {
    if (data) {
      setData({ data });
    }
    return () => {
      clearData();
    };
  }, [data]);

  const handlePreviousClick = () => {
    clearData();
    goToEngagementTemplateListRoute(history);
  };

  const handleHoverItem = (id) => {
    setSelectedId(id);
  };

  const handleNextClick = () => {
    setSaveLoading(true);
    saveForm()
      .then((res) => {
        goToEngagementTemplateListRoute(history);

        dispatch(showSuccessSnackbar(templateId === NEW_TEMPLATE ? 'Template Created.' : 'Template Updated'));
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message || 'Something went wrong!')))
      .finally(() => setSaveLoading(false));
  };

  const saveForm = () => {
    const data = {
      ...templateData,
      templateQuestions: insertDisplayOrderInArray(templateData.templateQuestions),
    };
    const saveStructure = templateId && templateId !== NEW_TEMPLATE ? updateTemplateStructure : createTemplateStructure;
    return saveStructure({ templateId, body: data });
  };

  const handleAddQuestion = ({ question, insertionPosition }) => {
    const tempQuestion = EngagementTemplateQuestion.fromJSON({
      question,
      questionType: question.questionType,
      isRequired: true,
    });
    addQuestion({ question: tempQuestion, insertionPosition });
  };

  const handleRemoveQuestion = ({ index }) => {
    removeQuestion({ index });
  };

  const handleChangeData = ({ param, value }) => {
    const newData = { ...templateData, [param]: value };
    setData({ data: newData });
  };

  const handleQuestionDataChange = ({ param, value, index }) => {
    const newQuestionList = [...templateQuestions];
    newQuestionList[index][param] = value;
    setQuestions({ questions: newQuestionList });
  };

  const handleQuestionOrderChange = ({ index, action }) => {
    let newQuestionList = [];
    if (action === QUESTION_ORDER_ACTIONS.MOVE_UP) {
      newQuestionList = swapElementsInArray(templateQuestions, index - 1, index);
    } else {
      newQuestionList = swapElementsInArray(templateQuestions, index + 1, index);
    }
    setQuestions({ questions: newQuestionList });
  };

  const openQuestionSidepanel = ({ insertionPosition }) => {
    //   We insert question just below the question from where sidepanel was invoked.
    showSidepanel({
      onSuccess: handleAddQuestion,
      insertionPosition,
    });
  };

  const handlePreviewClick = () => {
    saveForm()
      .then((res) => {
        const path = checkNewNavEnabled()
          ? getDeerEngagementTemplatePreviewRoute(res.id)
          : routes.template.engagement.preview.replace(':templateId', res.id);
        window.open(path, '_blank');
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message || 'Something went wrong!')));
  };

  const getIsActionDisabled = () => templateQuestions.length === 0 || !name || isEditable === false;

  useEffect(() => {
    dispatch(setL2Visible({ isL2Visible: false }));
  }, []);

  const headerActions = [
    {
      text: 'Preview',
      color: 'white',
      borderColor: getIsActionDisabled() || saveLoading ? '' : 'primary',
      fontColor: 'primary',
      onClick: handlePreviewClick,
      disabled: getIsActionDisabled() || saveLoading,
      icon: <PreviewIcon size="fixed12px" color={getIsActionDisabled() || saveLoading ? '' : 'primary'} />,
    },
  ];

  const footerActions = [
    {
      text: 'Back',
      color: 'white',
      borderColor: 'primary',
      fontColor: 'primary',
      onClick: handlePreviousClick,
      disabled: saveLoading,
    },
    {
      text: templateId === NEW_TEMPLATE ? 'Create' : 'Save',
      color: 'blue',
      borderColor: 'primary',
      fontColor: 'white',
      onClick: handleNextClick,
      disabled: getIsActionDisabled() || saveLoading,
    },
  ];

  if (isError) {
    return (
      <BodyContainer>
        <APIError />
      </BodyContainer>
    );
  }
  if (isLoading) {
    return <ListSkeletonLoader />;
  }

  if (!templateQuestions) {
    return (
      <BodyContainer>
        <EmptyError />
      </BodyContainer>
    );
  }

  return (
    <FlexContainer>
      <HeaderActions
        actions={headerActions}
        primaryText={
          <HeaderPrimaryTextComponent
            value={name}
            handleChange={(e) => handleChangeData({ param: 'name', value: e.target.value })}
          />
        }
        showBackButton
        onBackClick={clearData}
      />
      {isEditable === false && (
        <Box w="100%" justifyContent="center" bg="#FCE6E6" alignItems="center" padding="12px" display="flex">
          <NeedAttention mr="14px" boxSize="12px" color="#C62C02" />
          <TextLabel color="#C62C02">
            This template is being used in active surveys. You cannot edit this template but you can create a duplicate
            of the template and edit it.
          </TextLabel>
        </Box>
      )}
      <BodyContainer>
        <StyledContainer>
          <Typography11 topGutter={4} bottomGutter={2}>
            Questions
          </Typography11>
          {templateQuestions.length > 0 ? (
            templateQuestions?.map((ques, idx) => {
              const keyId = ques.id ?? ques.uuid;
              return (
                <EngagementQuestionRow
                  idx={idx}
                  templateQuestion={ques}
                  handleHoverItem={handleHoverItem}
                  selectedId={selectedId}
                  handleRemoveQuestion={handleRemoveQuestion}
                  handleChangeOrder={handleQuestionOrderChange}
                  handleQuestionDataChange={handleQuestionDataChange}
                  handleAddQuestionClick={openQuestionSidepanel}
                  isLastQuestion={idx === templateQuestions.length - 1}
                  key={`engagement-question-${keyId}`}
                  uuid={ques.uuid}
                  isAllowEdit={isEditable}
                />
              );
            })
          ) : (
            <NoQuestionsScreen addQuestionHandler={openQuestionSidepanel} />
          )}
        </StyledContainer>
      </BodyContainer>
      <FooterActions
        actions={footerActions}
        leftComponent={
          isNetchexLogin ? null : (
            <StyledLink href={KH_LINKS.ENGAGEMENT_TEMPLATES.url} target="_blank">
              <Typography13 display="inline" color={theme.palette.text.blackv1}>
                How to setup your Pulse Survey template?{' '}
              </Typography13>
              <Typography13 display="inline" weight={400} color={theme.palette.text.label}>
                (2 mins read)
              </Typography13>
            </StyledLink>
          )
        }
      />
    </FlexContainer>
  );
};

export default EngagementTemplateForm;
